/**
 * GraphQL Photo Mutation Hooks
 *
 * Hooks for photo operations: upload URL, create, delete, AI critique.
 */

import { useMutation, gql } from '@apollo/client';
import logger from '../../utils/logger';

// GraphQL Mutations
const GET_UPLOAD_URL_MUTATION = gql`
  mutation GetUploadUrl($input: GetUploadUrlInput!) {
    getUploadUrl(input: $input) {
      uploadUrl
      key
    }
  }
`;

const CREATE_PHOTO_MUTATION = gql`
  mutation CreatePhoto($input: CreatePhotoInput!) {
    createPhoto(input: $input) {
      id
      globalId
      title
      userId
    }
  }
`;

const DELETE_PHOTO_MUTATION = gql`
  mutation DeletePhoto($input: DeletePhotoInput!) {
    deletePhoto(input: $input) {
      success
      message
    }
  }
`;

const GENERATE_AI_CRITIQUE_MUTATION = gql`
  mutation GenerateAICritique($input: GenerateAICritiqueInput!) {
    generateAICritique(input: $input) {
      critique
    }
  }
`;

// Types
interface UploadUrlPayload {
  uploadUrl: string;
  key: string;
}

interface PhotoPayload {
  id: string;
  globalId: string;
  title: string;
  userId: string;
}

interface SuccessPayload {
  success: boolean;
  message?: string;
}

interface AICritiquePayload {
  critique: string;
}

// Input types
interface GetUploadUrlInput {
  filename: string;
  contentType: string;
}

interface CreatePhotoInput {
  title: string;
  filename: string;
  storageKey: string;
  fileSize: number;
  mimeType: string;
  collectionId?: string;
}

interface DeletePhotoInput {
  id: string;
}

interface GenerateAICritiqueInput {
  photoId: string;
}

/**
 * Hook for getting upload URL
 */
export function useGetUploadUrlGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { getUploadUrl: UploadUrlPayload },
    { input: GetUploadUrlInput }
  >(GET_UPLOAD_URL_MUTATION);

  const getUploadUrl = async (input: GetUploadUrlInput) => {
    logger.info('Photos', 'Getting upload URL via GraphQL', { filename: input.filename });

    const result = await mutate({ variables: { input } });

    return result.data?.getUploadUrl;
  };

  return {
    getUploadUrl,
    data: data?.getUploadUrl,
    loading,
    error,
  };
}

/**
 * Hook for creating a photo
 */
export function useCreatePhotoGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { createPhoto: PhotoPayload },
    { input: CreatePhotoInput }
  >(CREATE_PHOTO_MUTATION);

  const createPhoto = async (input: CreatePhotoInput) => {
    logger.info('Photos', 'Creating photo via GraphQL', { title: input.title });

    const result = await mutate({ variables: { input } });

    if (result.data?.createPhoto) {
      logger.info('Photos', 'Photo created', { id: result.data.createPhoto.id });
    }

    return result.data?.createPhoto;
  };

  return {
    createPhoto,
    data: data?.createPhoto,
    loading,
    error,
  };
}

/**
 * Hook for deleting a photo
 */
export function useDeletePhotoGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { deletePhoto: SuccessPayload },
    { input: DeletePhotoInput }
  >(DELETE_PHOTO_MUTATION);

  const deletePhoto = async (id: string) => {
    logger.info('Photos', 'Deleting photo via GraphQL', { id });

    const result = await mutate({ variables: { input: { id } } });

    return result.data?.deletePhoto;
  };

  return {
    deletePhoto,
    data: data?.deletePhoto,
    loading,
    error,
  };
}

/**
 * Hook for generating AI critique
 */
export function useGenerateAICritiqueGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { generateAICritique: AICritiquePayload },
    { input: GenerateAICritiqueInput }
  >(GENERATE_AI_CRITIQUE_MUTATION);

  const generateAICritique = async (photoId: string) => {
    logger.info('Photos', 'Generating AI critique via GraphQL', { photoId });

    const result = await mutate({ variables: { input: { photoId } } });

    return result.data?.generateAICritique;
  };

  return {
    generateAICritique,
    data: data?.generateAICritique,
    loading,
    error,
  };
}

/**
 * Combined hook for all photo mutations
 */
export function usePhotoMutationsGraphQL() {
  return {
    useGetUploadUrl: useGetUploadUrlGraphQL,
    useCreatePhoto: useCreatePhotoGraphQL,
    useDeletePhoto: useDeletePhotoGraphQL,
    useGenerateAICritique: useGenerateAICritiqueGraphQL,
  };
}
